'use client';

import { ProductCard } from '@components/Product/ProductCard';
import { ProductCardProps } from '@components/Product/ProductCard.types';
import { useCurrency } from '@contexts/CurrencyContext';
import { useEntitlements } from '@contexts/EntitlementsContext';
import { useProductLists } from '@contexts/ProductListsContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { AtomicContext } from '@contexts/coveo/AtomicContext';
import { Result, buildInteractiveResult } from '@coveo/atomic-react';
import {
  getActiveSale,
  getDiscountPercent,
  getSalePrice,
} from '@lib/coveo/sale';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { Sale, SaleType } from '@lib/types/Sale';
import { Tag } from '@lib/types/common/Tag';
import { getParentCategory } from '@lib/util/category';
import * as Sentry from '@sentry/nextjs';
import { useContext } from 'react';

type Props = {
  result: Result;
  allProductTags?: Tag[];
  context: AtomicContext;
  className?: string;
};

const VERIFIED_SOLUTION_TAG_SLUG = 'vsp';

export const HeadlessProductCardWrapper = ({
  result,
  context,
  className,
}: Props) => {
  const { searchEngine } = useContext(context);
  const { locale } = useRouter();
  const { currency } = useCurrency();
  const { getUserEntitlement } = useEntitlements();
  const { isProductInList, toggleProductList } = useProductLists();
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;

  if (!searchEngine) {
    return null;
  }

  const interactiveResultController = buildInteractiveResult(searchEngine, {
    options: { result },
  });

  const {
    ec_name,
    ec_product_id,
    ec_thumbnails,
    publisher_id,
    publisher_name,
    ec_rating,
    ec_rating_count,
    ec_price,
    ec_sale,
    ec_flash_teaser_starts_at,
    ec_category,
    ec_is_new_release,
    ec_tags,
  } = result.raw;
  const parentCategory = getParentCategory(ec_category as string[] | undefined);
  let sales: Sale[] = [];
  try {
    sales = ec_sale ? (JSON.parse(ec_sale as string) as Sale[]) : [];
  } catch (e) {
    Sentry.captureException(e);
  }
  const sale = getActiveSale(sales, ec_flash_teaser_starts_at as string);

  const productId = ec_product_id
    ? JSON.parse(ec_product_id as string).toString()
    : '';
  const entitlement = getUserEntitlement(productId);

  const isVerifiedSolution =
    (ec_tags as string[])?.includes(VERIFIED_SOLUTION_TAG_SLUG) ?? false;

  const commonProps: ProductCardProps = {
    name: ec_name as string,
    productId,
    imageUrl: ec_thumbnails as string,
    publisher: {
      name: publisher_name as string,
      id: publisher_id as string,
    },
    rating: ec_rating as number,
    ratingCount: ec_rating_count as number,
    price: {
      currentPrice: sale ? getSalePrice(sale, currency) : (ec_price as number),
      originalPrice: ec_price as number,
      currency,
      discountPercent: sale?.discount.value
        ? getDiscountPercent(sale)
        : undefined,
    },
    isVerifiedSolution,
    showFavorite: isProductListsEnabled,
    isFavorite: isProductListsEnabled ? isProductInList(productId) : undefined,
    sale: sale
      ? {
          type: sale.type ?? SaleType.Sale,
          dates: {
            starts: new Date(sale.dates.starts),
            ends: new Date(sale.dates.ends),
          },
        }
      : undefined,
    entitlement,
    locale,
    extraEventHandlers: {
      onClick: () => interactiveResultController.select(),
      onContextMenu: () => interactiveResultController.select(),
      onMouseUp: () => interactiveResultController.select(),
      onMouseDown: () => interactiveResultController.select(),
      onTouchStart: () => interactiveResultController.beginDelayedSelect(),
      onTouchEnd: () => interactiveResultController.cancelPendingSelect(),
      onFavorite: isProductListsEnabled
        ? () => toggleProductList({ id: productId, name: ec_name as string })
        : undefined,
    },
    className,
  };

  return (
    <ProductCard
      {...commonProps}
      category={parentCategory}
      isNewRelease={ec_is_new_release as boolean}
    />
  );
};
